import { Application } from '@/types/application';
import { ConfigurationEnum, Metadata } from '@/types/metadata/metadata';
import { Prompt } from '@/types/prompt';
import { Subroutine } from '@/types/subroutine';
import { KeyValue, Variable } from '@/types/variable';
import { isArray, isConfigurationEnum, isKeyValue, isNumber, isString } from './typeGuards';

export const getNewPromptId = (prompts: Prompt[]): number => {
  const promptIds = prompts.map((prompt) => prompt.id);
  const highestId = Math.max(...promptIds, 0);
  return highestId + 1;
};

export const promptSearchOptions = [
  { name: 'label', label: 'Label' },
  { name: 'path', label: 'Path' },
  { name: 'file', label: 'File Name' }
];

export const filterPrompts = (prompts: Prompt[] | undefined, searchQuery: string) =>
  prompts?.filter((prompt) => {
    // Return true (include item in the filtered data) if query is empty or it matches the item's name
    return (
      prompt.label?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      prompt.file?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (isString(prompt.path)
        ? prompt.path?.toLowerCase().includes(searchQuery.toLowerCase())
        : prompt.path?.name.toLowerCase().includes(searchQuery.toLowerCase()))
    );
  });

const recursivePromptSections = (config: ConfigurationEnum, blockId: string, application: Application): number[] => {
  let result: number[] = [];
  config.sections?.find((section) => {
    section.forEach((innerSection) => {
      const sectionValue = application.blocks.details[blockId][innerSection.value];
      if (innerSection.type === 'prompts' && isArray(sectionValue)) {
        sectionValue.forEach((prompt: Variable | string) => {
          if (isNumber(prompt)) {
            result.push(prompt);
          }
        });
      }
      if (innerSection.type === 'kvvaluelist' && isArray(sectionValue)) {
        sectionValue.forEach((sectionValue: KeyValue<string | number> | string) => {
          if (isKeyValue(sectionValue) && isArray(sectionValue.value)) {
            sectionValue.value.forEach((promptValue) => {
              if (isNumber(promptValue)) {
                result.push(promptValue);
              }
            });
          }
        });
      }
      if (innerSection.sections) {
        result = recursivePromptSections(innerSection, blockId, application);
      }
    });
  });

  return result;
};

export const promptIsInUse = (
  application: Application,
  metadata: Metadata,
  currentSubroutine: Subroutine | undefined,
  prompt: Prompt
): inUse => {
  let result: inUse = undefined;
  Object.entries(application.blocks.details).forEach(([blockId, _]) => {
    const metaBlock = metadata.blocks.find(({ type }) => type === application.blocks.nodes[blockId]?.type);

    metaBlock?.configuration.forEach((config) => {
      if (result === 'green') return;

      const inCurrentSubroutine = currentSubroutine?.nodes.includes(Number(blockId));

      const configValue = application.blocks.details[blockId][config.value];
      if (config.type === 'prompts' && isArray(configValue)) {
        configValue.forEach((value: Variable | number) => {
          if (isNumber(value) && value === prompt.id) {
            result = inCurrentSubroutine ? 'green' : 'grey';
          }
        });
      }
      if (isConfigurationEnum(config) && config.sections) {
        const foundSection = recursivePromptSections(config, blockId, application);
        foundSection.forEach((section) => {
          if (isNumber(section) && section === prompt.id) {
            result = inCurrentSubroutine ? 'green' : 'grey';
          }
        });
      }
    });
  });

  return result;
};
